import { useTranslation } from "react-i18next";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";
import { Card, CardContent } from "@/ui/components/ui/card";
import { Machine } from "@/machines/types/machine";
import { TruckEventOverview } from "@/machines/types/event";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore";

interface MachineDetailKpiSectionProps {
  machine: Machine;
  truckEventsOverview?: TruckEventOverview;
}

const MachineDetailKpiSection = ({ machine, truckEventsOverview }: MachineDetailKpiSectionProps) => {
  const { t } = useTranslation("machines");
  const { value } = useDateTimeRangeStore();

  const durationHours = Math.max(
    0.0001,
    (value.toDate.getTime() - value.fromDate.getTime()) / (1000 * 60 * 60)
  );

  const metadata = truckEventsOverview?.metadata;
  const truckLoadingData = metadata?.truckLoading;
  const waitingForTruckData = metadata?.waitingForTruck;

  // Count total trucks from graphData
  const totalTrucks = truckEventsOverview?.graphData?.length ?? 0;

  const kpiItems = truckLoadingData
    ? [
        {
          label: t("detail.kpis.totalTrucksLoaded"),
          value: totalTrucks,
        },
        {
          label: t("detail.kpis.trucksPerHour"),
          value: totalTrucks > 0
            ? formatNumber(totalTrucks / durationHours, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
            : "-",
        },
        {
          label: t("detail.kpis.avgLoadingTime"),
          value: truckLoadingData.avgCycleTime != null
            ? t("units.minutes", { value: formatNumber(truckLoadingData.avgCycleTime, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) })
            : "-",
        },
        {
          label: t("detail.kpis.avgTimeBetweenTrucks"),
          value: waitingForTruckData?.avgWaitTime != null
            ? t("units.minutes", { value: formatNumber(waitingForTruckData.avgWaitTime, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) })
            : "-",
        },
        {
          label: t("detail.kpis.totalBuckets"),
          value: truckLoadingData.bucketCount ?? "-",
        },
        {
          label: t("detail.kpis.totalBoulders"),
          value: truckLoadingData.boulderCount ?? "-",
        },
        {
          label: t("detail.kpis.longCycles"),
          value: truckLoadingData.longCyclesCnt
            ? t("detail.kpis.countWithPercent", {
                value: formatNumber(truckLoadingData.longCyclesCnt),
                percent: formatNumber(truckLoadingData.longCyclesPerc, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
              })
            : "-",
        },
      ]
    : [];

  return (
    <div className="flex-shrink-0">
      <div className="w-full bg-sidebar/50 border-y">
        <div className="flex gap-4 py-3 px-4">
          {kpiItems.map((item, index) => (
            <Card
              key={index}
              className="flex-1 bg-background/50 border-muted"
            >
              <CardContent className="p-2 flex flex-col gap-1 justify-between">
                <span className="text-xs text-muted-foreground text-center">
                  {item.label}
                </span>
                <span className="text-lg font-bold text-center">
                  {item.value}
                </span>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MachineDetailKpiSection;
